import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Box } from '@mui/material';

import type { ISplitBarProps } from '../../../types';

const SplitBar = memo(({ onResize, index, paneRefs }: ISplitBarProps) => {
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const initialWidths = useRef<number[]>([]);

  // useEffect to initialize the initial widths of the panes when the component mounts or paneRefs change

  useEffect(() => {
    if (paneRefs.current && paneRefs.current.length > 0) {
      initialWidths.current = paneRefs.current.map((ref) => ref?.offsetWidth || 100);
    }
  }, [paneRefs.current]);

  // useCallback to handle the mouse down event on the split bar

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setIsResizing(true);
      setStartX(e.clientX); // Store the initial mouse X coordinate

      if (paneRefs.current && paneRefs.current.length > 0) {
        // Re-initialize the initial widths when resizing starts
        initialWidths.current = paneRefs.current.map((ref) => ref?.offsetWidth || 100);
      }
    },

    [paneRefs]
  );

  // useCallback to handle the mouse move event during resizing
  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isResizing && paneRefs.current && paneRefs.current.length > 0) {
        const deltaX = e.clientX - startX;
        const newWidths = [...initialWidths.current];

        if (index >= 0 && index < paneRefs.current.length - 1) {
          // Calculate the new widths for the left and right panes
          let leftWidth = initialWidths.current[index] + deltaX;
          let rightWidth = initialWidths.current[index + 1] - deltaX;
          const minWidth = 10;

          const totalWidth = initialWidths.current[index] + initialWidths.current[index + 1];
          leftWidth = Math.max(minWidth, leftWidth);
          rightWidth = Math.max(minWidth, rightWidth);
          // Ensure the total width remains consistent
          if (leftWidth + rightWidth > totalWidth) {
            leftWidth = Math.min(leftWidth, totalWidth - minWidth);
            rightWidth = totalWidth - leftWidth;
          }
          newWidths[index] = leftWidth;
          newWidths[index + 1] = rightWidth;
        }

        onResize(newWidths);
      }
    },

    [isResizing, startX, index, paneRefs, onResize]
  );

  // useCallback to handle the mouse up event, ending the resizing process
  const onMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  const removeEventListeners = useCallback(() => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove, onMouseUp]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    } else {
      removeEventListeners();
    }
    return () => {
      removeEventListeners();
    };
  }, [isResizing, onMouseMove, onMouseUp]);

  return (
    <Box
      onMouseDown={onMouseDown}
      sx={{
        cursor: 'e-resize',
        background: '#f2f4f7',
        width: '5px',
      }}
    />
  );
});

SplitBar.displayName = 'SplitBar';
export default SplitBar;
